import React, { useEffect } from "react";
import { Header } from "../../components/Header";
import { Footer } from "../../components/Footer";
import { injectJsonLd, breadcrumbSchema } from "../../seo";

const features = [
  {
    title: "Table Reservations 24/7",
    description:
      "Let customers book a table any time — via your website or WhatsApp. Your AI checks availability and confirms bookings instantly without a phone call.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
      </svg>
    ),
  },
  {
    title: "Menu & Dietary Questions",
    description:
      "Customers ask about halal certification, allergens, vegetarian options, and daily specials. Your AI answers instantly so your staff can focus on the kitchen.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
      </svg>
    ),
  },
  {
    title: "Operating Hours & Location",
    description:
      "Stop answering \"What time do you open?\" on WhatsApp. Your AI handles hours, public holiday schedules, and directions across all your branches.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: "Online Order FAQs",
    description:
      "Handle delivery time estimates, minimum order amounts, and payment method questions for your GrabFood, Foodpanda, or own-delivery orders.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
      </svg>
    ),
  },
  {
    title: "Event & Private Dining Enquiries",
    description:
      "Capture leads for corporate events, birthday dinners, and private functions. Your AI collects party size, date, and budget before passing to your team.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
      </svg>
    ),
  },
  {
    title: "BM, English & Chinese",
    description:
      "Serve customers in Bahasa Malaysia, English, and Mandarin. Essential for Malaysian F&B businesses serving a multilingual customer base.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" />
      </svg>
    ),
  },
];

const useCases = [
  {
    title: "Weekend Rush Enquiries",
    description: "Handle the spike of reservation and menu questions on Friday evenings without a single staff member on the phone.",
  },
  {
    title: "Ramadan & Festive Seasons",
    description: "Manage the flood of buka puasa package and CNY reunion dinner enquiries automatically, capturing every lead.",
  },
  {
    title: "Multi-Branch Management",
    description: "One chatbot handles enquiries for all your outlets. Route customers to the right branch automatically.",
  },
];

export function RestaurantsPage() {
  useEffect(() => {
    document.title = "AI Chatbot for Restaurants & F&B — Malaysia | Onyx AI";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Automate table reservations, menu questions & order enquiries for your restaurant. AI chatbot in BM, EN & Chinese for Malaysian F&B businesses."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://onyxai.chat/solutions/restaurants");
    }
    return injectJsonLd("breadcrumb-jsonld", breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Solutions", path: "/solutions" },
      { name: "Restaurants", path: "/solutions/restaurants" },
    ]));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a12]">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-[#6a5a7a] mb-10">
            <a href="/" className="hover:text-[#a078d0] transition-colors">Home</a>
            <span>/</span>
            <span className="text-[#6a5a7a]">Solutions</span>
            <span>/</span>
            <span className="text-[#8a7a9a]">Restaurants & F&B</span>
          </nav>

          <div className="mb-16">
            <p className="text-[#a078d0] text-sm font-medium uppercase tracking-widest mb-4">
              Restaurants & F&B
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-5">
              AI Chatbot for Restaurants & F&B Businesses
            </h1>
            <p className="text-[#8a7a9a] text-lg max-w-2xl">
              Handle reservations, menu questions, and delivery enquiries automatically. Free your staff from the phone so they can focus on the dining experience.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="/signup"
                className="inline-block px-7 py-3 bg-[#7b5ea7] hover:bg-[#6a4f96] text-white font-medium rounded-lg transition-colors"
              >
                Start Free Trial
              </a>
              <a
                href="/pricing"
                className="inline-block px-7 py-3 border border-[#2a2040] hover:border-[#a078d0] text-[#8a7a9a] hover:text-white font-medium rounded-lg transition-colors"
              >
                View Pricing
              </a>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-white mb-6">What Your AI Chatbot Handles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {features.map((feature) => (
              <div key={feature.title} className="p-6 rounded-xl onyx-card onyx-glow-hover">
                <div className="w-10 h-10 rounded-lg bg-[#a078d0]/10 flex items-center justify-center text-[#c0a0e0] mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-[#8a7a9a] leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-white mb-6">Common Use Cases</h2>
          <div className="grid md:grid-cols-3 gap-5 mb-16">
            {useCases.map((item) => (
              <div key={item.title} className="p-5 rounded-xl onyx-card">
                <h3 className="text-base font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-[#8a7a9a] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="p-8 rounded-2xl onyx-card text-center">
            <h2 className="text-2xl font-bold text-white mb-3">Let your AI handle enquiries while you focus on the food.</h2>
            <p className="text-[#8a7a9a] mb-6">Set up in under 5 minutes. No developers needed.</p>
            <a
              href="/signup"
              className="inline-block px-8 py-3 bg-[#7b5ea7] hover:bg-[#6a4f96] text-white font-medium rounded-lg transition-colors"
            >
              Get Started Free
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
